import { useEffect, useRef, useState } from 'react';
import { Check, Loader2, MapPin } from 'lucide-react';

import { suggestAddress, type AddressSuggestion } from '@/services/admin';

/**
 * Адрес заведения с подсказками DaData.
 *
 * Свободный ввод оставлен — никого не заставляем выбирать из списка. Но пока
 * адрес введён руками, у нас нет города и координат, и заведение не попадает
 * на публичную карту лендинга: строке «Победы 85/2» без города DaData уверенно
 * отвечает Москвой, поэтому такому городу карта не верит. Выбор подсказки
 * заполняет город и координаты дома сразу, и точка встаёт на своё место.
 *
 * Подсказки идут через наш бэкенд (/api/admin/geo/suggest-address), чтобы ключ
 * DaData не попал в бандл.
 */
export function AddressField({
  value,
  city,
  hasCoords,
  onChange,
}: {
  value: string;
  city: string | null;
  hasCoords: boolean;
  /** city/lat/lon приходят только при выборе подсказки; при ручном вводе — null. */
  onChange: (next: {
    address: string;
    city: string | null;
    lat: number | null;
    lon: number | null;
  }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AddressSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  // Ввод, который пользователь печатает сам. Пока он null — показываем value.
  const [typed, setTyped] = useState<string | null>(null);

  const shown = typed ?? value;

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Дебаунс: DaData — платный внешний вызов, на каждый символ ходить незачем.
  useEffect(() => {
    const q = (typed ?? '').trim();
    if (typed === null || q.length < 3) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await suggestAddress(q);
        if (!cancelled) setItems(res.suggestions);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [typed]);

  const choose = (s: AddressSuggestion) => {
    onChange({ address: s.value, city: s.city, lat: s.lat, lon: s.lon });
    setTyped(null);
    setOpen(false);
    setItems([]);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open || items.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % items.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h - 1 + items.length) % items.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const s = items[highlight];
      if (s) choose(s);
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      setOpen(false);
    }
  };

  const resolved = !!city && hasCoords;

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          className="input pl-8 pr-9"
          placeholder="Город, улица, дом"
          value={shown}
          onChange={(e) => {
            const address = e.target.value;
            setTyped(address);
            setHighlight(0);
            setOpen(true);
            // Ручная правка обнуляет город и координаты: они относились к
            // прежнему адресу и молча увели бы точку на карте не туда.
            onChange({ address, city: null, lat: null, lon: null });
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2">
          {loading ? (
            <Loader2 size={14} className="text-slate-400 animate-spin" />
          ) : resolved ? (
            <Check size={14} className="text-emerald-600" />
          ) : null}
        </span>
      </div>

      <div className="text-xs mt-1">
        {resolved ? (
          <span className="text-slate-500">
            Город для карты: <span className="font-medium text-slate-700">{city}</span>
          </span>
        ) : (
          <span className="text-amber-600">
            Выберите адрес из подсказок — иначе заведение не появится на карте на сайте
          </span>
        )}
      </div>

      {open && (typed ?? '').trim().length >= 3 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-auto">
          {items.length === 0 ? (
            <div className="px-3 py-3 text-sm text-slate-400">
              {loading ? 'Ищем адрес…' : 'Ничего не найдено'}
            </div>
          ) : (
            items.map((s, i) => (
              <button
                key={`${s.value}-${i}`}
                type="button"
                className={`w-full text-left px-3 py-2 flex items-start gap-2 ${
                  highlight === i ? 'bg-indigo-50' : 'hover:bg-slate-50'
                }`}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => choose(s)}
              >
                <MapPin size={14} className="text-slate-400 shrink-0 mt-0.5" />
                <span className="flex-1 min-w-0 text-sm">{s.value}</span>
                {!s.precise && (
                  <span className="text-[10px] uppercase tracking-wide text-amber-600 shrink-0 mt-0.5">
                    без дома
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
